import { useEffect, useMemo, useState, type JSX } from 'react';
import { ChevronDown, ChevronRight, Megaphone } from 'lucide-react';
import { CampaignBrief } from '@/components/CampaignBrief';
import { Button } from '@/components/ui/button';
import { briefingStartsOpen, folderKey } from '@/hooks/useDiscoveryQueue';
import type { DiscoveryFolderSummary } from '@/api/types';
import { cn } from '@/lib/utils';

/**
 * Per-folder memory of whether the briefing was open, so it shows in full
 * the first time someone sees a folder and stays out of the way after.
 * Browser storage can be unavailable (private mode, blocked site data), in
 * which case every visit is a first visit: open.
 */
const STORAGE_KEY = 'spade-queue-briefings';

function readStored(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeStored(key: string, value: 'open' | 'closed'): void {
  try {
    const all = readStored();
    all[key] = value;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Nothing to do: the strip still works, it just won't remember.
  }
}

/**
 * Open/closed state for one strip, remembered per key. The starting state is
 * read once per key during render; the effect only records the first
 * sighting, so a double-run effect (StrictMode) can't flip what's on screen.
 */
function useRememberedOpen(key: string, firstVisitOpen: boolean): [boolean, (open: boolean) => void] {
  const initial = useMemo(() => {
    const stored = readStored()[key];
    return stored === undefined ? firstVisitOpen : briefingStartsOpen(stored);
  }, [key, firstVisitOpen]);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    // First sighting: shown as `firstVisitOpen` now, collapsed next time.
    if (readStored()[key] === undefined) writeStored(key, 'closed');
  }, [key]);
  return [
    chosen[key] ?? initial,
    (next: boolean) => {
      setChosen((prev) => ({ ...prev, [key]: next }));
      writeStored(key, next ? 'open' : 'closed');
    },
  ];
}

/**
 * "Briefing for <folder>": what to pitch and which campaign link to paste.
 * A strip, not a callout, so the leads stay above the fold once read.
 */
export function FolderBriefingStrip({ folder }: { folder: DiscoveryFolderSummary }): JSX.Element {
  const key = folderKey(folder.sourceFolderPublicId);
  const [open, setOpen] = useRememberedOpen(key, true);
  const name = folder.sourceFolderName ?? 'Unfiled';
  const panelId = `briefing-${key}`;

  return (
    <section className="panel rounded-xl border-brand/30">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-accent/40"
      >
        <Megaphone className="h-4 w-4 shrink-0 text-brand" strokeWidth={2} />
        <span className="truncate">Briefing for {name}</span>
        {!open && (
          <span className="hidden min-w-0 flex-1 truncate text-xs font-normal text-muted-foreground sm:block">
            {folder.recommendedCampaigns ?? folder.pitchNotes ?? ''}
          </span>
        )}
        <ChevronDown
          className={cn('ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div id={panelId} className="space-y-4 border-t border-border px-4 py-4">
          <BriefingBody folder={folder} />
        </div>
      )}
    </section>
  );
}

function BriefingBody({ folder }: { folder: DiscoveryFolderSummary }): JSX.Element {
  return (
    <>
      <CampaignBrief campaigns={folder.campaigns ?? []} />
      {folder.recommendedCampaigns && (
        <div>
          <h3 className="mb-1 text-xs font-semibold text-brand">Best campaigns</h3>
          <p className="whitespace-pre-wrap text-sm font-medium leading-relaxed">{folder.recommendedCampaigns}</p>
        </div>
      )}
      {folder.pitchNotes && (
        <div>
          <h3 className="mb-1 text-xs font-semibold text-brand">How to pitch them</h3>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{folder.pitchNotes}</p>
        </div>
      )}
    </>
  );
}

/**
 * With "All folders" selected there's no single briefing to show, so this
 * lists the ones that exist, one line each, with a way into the folder.
 * Collapsed until opened; remembered like the per-folder strips.
 */
export function AllBriefingsStrip({
  folders,
  newKeys,
  onOpenFolder,
}: {
  folders: DiscoveryFolderSummary[];
  /** Folder keys showing NEW on their chip; counted in the header. */
  newKeys?: ReadonlySet<string>;
  onOpenFolder: (folderPublicId: string | null) => void;
}): JSX.Element | null {
  const [open, setOpen] = useRememberedOpen('__all__', false);
  if (folders.length === 0) return null;
  const newCount = newKeys ? folders.filter((f) => newKeys.has(folderKey(f.sourceFolderPublicId))).length : 0;

  return (
    <section className="panel rounded-xl">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="briefings-all"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-accent/40"
      >
        <Megaphone className="h-4 w-4 shrink-0 text-brand" strokeWidth={2} />
        Briefings
        <span className="font-normal text-muted-foreground">
          {folders.length} folder{folders.length === 1 ? '' : 's'}
        </span>
        {newCount > 0 && (
          <span className="rounded-full bg-warning/15 px-1.5 py-0.5 text-xs font-medium text-pending-strong ring-1 ring-warning/40">
            {newCount} new
          </span>
        )}
        <ChevronDown
          className={cn('ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <ul id="briefings-all" className="divide-y divide-border border-t border-border">
          {folders.map((f) => {
            const summary =
              f.recommendedCampaigns ??
              f.pitchNotes ??
              (f.campaigns ?? []).map((c) => c.name).join(', ');
            return (
              <li key={folderKey(f.sourceFolderPublicId)} className="flex items-center gap-3 px-4 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{f.sourceFolderName ?? 'Unfiled'}</div>
                  <p className="truncate text-xs text-muted-foreground">{summary}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => onOpenFolder(f.sourceFolderPublicId)}>
                  Open
                  <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
